/* eslint-disable no-undef */
import { useState, useMemo, useCallback } from 'react';
import { StepDefinition } from 'components/PremiumWizard';
import { DrawingResult } from './useMemberLookupMap';
import {
  MAX_CUMULATIVE_DRAW_AREA_KM2,
  MAX_DRAW_FEATURE_COUNT
} from '../constants/geometryLimits';

export type MemberLookupWizardInputMode = 'upload' | 'draw' | null;

interface UploadedFile {
  name: string;
  size: string;
}

interface UseBydaMemberLookupWizardReturn {
  activeStep: number;
  steps: StepDefinition[];
  inputMode: MemberLookupWizardInputMode;
  uploadedFile: UploadedFile | null;
  drawnShapes: DrawingResult[];
  setInputMode: (mode: MemberLookupWizardInputMode) => void;
  setUploadedFile: (file: UploadedFile | null) => void;
  addDrawnShape: (result: DrawingResult) => void;
  updateDrawnShape: (index: number, result: DrawingResult) => void;
  removeDrawnShape: (index: number) => void;
  clearAllShapes: () => void;
  goNext: () => void;
  goBack: () => void;
  jumpToStep: (index: number) => void;
  resetWizard: () => void;
  canProceed: boolean;
}

const BASE_STEPS: StepDefinition[] = [
  { id: 'price', label: 'Price' },
  { id: 'area-input', label: 'Area input' },
  { id: 'payment', label: 'Payment' },
  { id: 'complete', label: 'Complete' }
];

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const useBydaMemberLookupWizard = (): UseBydaMemberLookupWizardReturn => {
  const [activeStep, setActiveStep] = useState(0);
  const [inputMode, setInputModeState] = useState<MemberLookupWizardInputMode>(
    null
  );
  const [uploadedFile, setUploadedFileState] = useState<UploadedFile | null>(
    null
  );
  const [drawnShapes, setDrawnShapes] = useState<DrawingResult[]>([]);

  const steps = useMemo(() => BASE_STEPS, []);

  const setInputMode = useCallback((mode: MemberLookupWizardInputMode) => {
    setInputModeState((current) => {
      if (current === mode) return current;
      setUploadedFileState(null);
      setDrawnShapes([]);
      return mode;
    });
  }, []);

  const setUploadedFile = useCallback((file: UploadedFile | null) => {
    setUploadedFileState(file);
  }, []);

  const addDrawnShape = useCallback((result: DrawingResult) => {
    setDrawnShapes((prev) => [...prev, result]);
  }, []);

  const updateDrawnShape = useCallback(
    (index: number, result: DrawingResult) => {
      setDrawnShapes((prev) =>
        prev.map((shape, i) => (i === index ? result : shape))
      );
    },
    []
  );

  const removeDrawnShape = useCallback((index: number) => {
    setDrawnShapes((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearAllShapes = useCallback(() => {
    setDrawnShapes([]);
  }, []);

  const totalDrawnAreaKm2 = useMemo(
    () => drawnShapes.reduce((sum, shape) => sum + shape.area, 0),
    [drawnShapes]
  );

  const canProceed = useMemo(() => {
    const currentStepId = steps[activeStep]?.id;
    switch (currentStepId) {
      case 'price':
        return true;
      case 'area-input':
        if (inputMode === 'upload') return uploadedFile !== null;
        if (inputMode === 'draw') {
          return (
            drawnShapes.length > 0 &&
            drawnShapes.length <= MAX_DRAW_FEATURE_COUNT &&
            totalDrawnAreaKm2 <= MAX_CUMULATIVE_DRAW_AREA_KM2
          );
        }
        return false;
      case 'payment':
        return true;
      case 'complete':
        return false;
      default:
        return true;
    }
  }, [
    activeStep,
    steps,
    inputMode,
    uploadedFile,
    drawnShapes,
    totalDrawnAreaKm2
  ]);

  const goNext = useCallback(() => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  }, [activeStep, steps.length]);

  const goBack = useCallback(() => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  }, [activeStep]);

  const jumpToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < steps.length) {
        setActiveStep(index);
      }
    },
    [steps.length]
  );

  const resetWizard = useCallback(() => {
    setActiveStep(0);
    setInputModeState(null);
    setUploadedFileState(null);
    setDrawnShapes([]);
  }, []);

  return {
    activeStep,
    steps,
    inputMode,
    uploadedFile,
    drawnShapes,
    setInputMode,
    setUploadedFile,
    addDrawnShape,
    updateDrawnShape,
    removeDrawnShape,
    clearAllShapes,
    goNext,
    goBack,
    jumpToStep,
    resetWizard,
    canProceed
  };
};

export { formatFileSize };
export default useBydaMemberLookupWizard;
